import React, { useCallback, useEffect, useState } from 'react'
import UploadButton from './components/UploadButton'
import DocumentViewer from './components/DocumentViewer'
import FieldsTable from './components/FieldsTable'
import DocumentList from './components/DocumentList'
import DropZone from './components/DropZone'
import {
  getDocument,
  listDocuments,
  processDocument,
  updateDocument,
} from './services/ocr-api'
import {
  DocumentDetail,
  DocumentListItem,
  OcrField,
  getPatientNameFromFields,
} from './types'
import { appStyles } from './App.styles'

type Page = 'list' | 'detail'

const App: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentListItem[]>([])
  const [selectedDetail, setSelectedDetail] = useState<DocumentDetail | null>(null)
  const [page, setPage] = useState<Page>('list')
  const [loadingList, setLoadingList] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadDocuments = useCallback(async () => {
    try {
      setError(null)
      const items = await listDocuments()
      setDocuments(items ?? [])
    } catch {
      setError('Failed to load documents from API')
    } finally {
      setLoadingList(false)
    }
  }, [])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const handleFilesSelected = async (files: File[]) => {
    for (const file of files) {
      const tempId = `uploading-${Date.now()}-${file.name}`

      setDocuments((prev) => [
        {
          id: tempId,
          filename: file.name,
          status: 'uploading',
          autoSubmit: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          approvedAt: null,
        },
        ...prev,
      ])

      try {
        setError(null)
        await processDocument(file)
        await loadDocuments()
      } catch {
        setError(`Failed to process ${file.name}`)
        setDocuments((prev) => prev.filter((d) => d.id !== tempId))
      }
    }
  }

  const handleSelectDocument = async (doc: DocumentListItem) => {
    if (doc.status === 'uploading') return

    setLoadingDetail(true)
    setError(null)
    setPage('detail')

    try {
      const detail = await getDocument(doc.id)
      setSelectedDetail(detail)
    } catch {
      setError('Failed to load document details')
      setPage('list')
      setSelectedDetail(null)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleBack = () => {
    setPage('list')
    setSelectedDetail(null)
    loadDocuments()
  }

  const handleSave = async (mappedFields: Record<string, OcrField>) => {
    if (!selectedDetail) return

    setIsSaving(true)
    setError(null)

    try {
      const updated = await updateDocument(selectedDetail.id, { mappedFields })
      setSelectedDetail(updated)
      await loadDocuments()
    } catch {
      setError('Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }

  const handleApprove = async (mappedFields: Record<string, OcrField>) => {
    if (!selectedDetail) return

    setIsSaving(true)
    setError(null)

    try {
      const updated = await updateDocument(selectedDetail.id, {
        mappedFields,
        status: 'approved',
      })
      setSelectedDetail(updated)
      await loadDocuments()
    } catch {
      setError('Failed to approve document')
    } finally {
      setIsSaving(false)
    }
  }

  const showDropZone = !loadingList && (documents?.length ?? 0) === 0

  return (
    <div style={appStyles.container}>
      <div style={appStyles.header}>
        <h1 style={appStyles.headerTitle}>NovoPath OCR</h1>
        {page === 'list' && (documents?.length ?? 0) > 0 && (
          <UploadButton onFilesSelected={handleFilesSelected} isLoading={false} />
        )}
        {page === 'detail' && (
          <button onClick={handleBack} style={appStyles.backButton}>
            ← Back to List
          </button>
        )}
        {selectedDetail && page === 'detail' && (
          <span style={appStyles.headerFilename}>
            {selectedDetail.filename} — {getPatientNameFromFields(selectedDetail.mappedFields)}
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: '8px 24px', backgroundColor: '#fdecea', color: '#c0392b' }}>
          {error}
        </div>
      )}

      {page === 'list' && (
        <div style={appStyles.listContainer}>
          {loadingList ? (
            <div style={{ padding: '24px', color: '#666' }}>Loading documents…</div>
          ) : showDropZone ? (
            <DropZone onFilesSelected={handleFilesSelected} />
          ) : (
            <DocumentList
              documents={documents ?? []}
              onSelectDocument={handleSelectDocument}
              selectedId={selectedDetail?.id}
            />
          )}
        </div>
      )}

      {page === 'detail' && loadingDetail && (
        <div style={{ padding: '24px', color: '#666' }}>Loading document…</div>
      )}

      {page === 'detail' && selectedDetail && !loadingDetail && (
        <div style={appStyles.content}>
          <div style={appStyles.leftPanel}>
            <DocumentViewer
              documentId={selectedDetail.id}
              mimeType={selectedDetail.mimeType}
            />
          </div>
          <div style={appStyles.rightPanel}>
            <FieldsTable
              documentId={selectedDetail.id}
              workflowStatus={selectedDetail.status}
              mappedFields={selectedDetail.mappedFields as Record<string, OcrField>}
              specimens={selectedDetail.specimens ?? []}
              insurance={selectedDetail.insurance ?? {}}
              evaluatedFields={selectedDetail.confidenceEvaluation.evaluatedFields}
              isSaving={isSaving}
              onSave={handleSave}
              onApprove={handleApprove}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default App
